import { useState, useEffect, useRef } from 'react';
import { Briefcase, Plus, Trash2, AlertCircle, Loader2, Search, Check, Building2 } from 'lucide-react';
import { KarrierepassetImportWorkExperience } from './KarrierepassetImportWorkExperience';

interface OccupationSearchResult {
  uri: string;
  title: string;
  title_no: string | null;
  title_en: string | null;
  code: string | null;
  essential_skill_count: number;
}

interface CareerEntry {
  id: string;
  esco_occupation_uri: string;
  occupation_title: string;
  from_month: string;          // YYYY-MM-DD (alltid -01)
  to_month: string | null;
  percentage: number;
  employer_name: string | null;
  notes: string | null;
}

const MONTHS_NB = ['jan', 'feb', 'mar', 'apr', 'mai', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'des'];

function fmtMonth(d: string | null): string {
  if (!d) return 'pågående';
  const [y, m] = d.split('-');
  return `${MONTHS_NB[parseInt(m, 10) - 1]} ${y}`;
}

function detectOverlaps(entries: CareerEntry[]): string[] {
  // Returnerer ID-er på rader som overlapper med minst én annen
  const overlapping = new Set<string>();
  for (let i = 0; i < entries.length; i++) {
    for (let j = i + 1; j < entries.length; j++) {
      const a = entries[i], b = entries[j];
      const aStart = a.from_month;
      const aEnd = a.to_month || '9999-12-01';
      const bStart = b.from_month;
      const bEnd = b.to_month || '9999-12-01';
      if (aStart <= bEnd && bStart <= aEnd) {
        overlapping.add(a.id);
        overlapping.add(b.id);
      }
    }
  }
  return Array.from(overlapping);
}

export function CareerProfileSection() {
  const [entries, setEntries] = useState<CareerEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Add-form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<OccupationSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedOccupation, setSelectedOccupation] = useState<OccupationSearchResult | null>(null);

  const [fromMonth, setFromMonth] = useState('');
  const [fromYear, setFromYear] = useState('');
  const [toMonth, setToMonth] = useState('');
  const [toYear, setToYear] = useState('');
  const [stillCurrentlyEmployed, setStillCurrentlyEmployed] = useState(false);
  const [percentage, setPercentage] = useState('100');
  const [employerName, setEmployerName] = useState('');
  const [saving, setSaving] = useState(false);

  const searchTimeout = useRef<number | null>(null);

  useEffect(() => {
    fetchEntries();
  }, []);

  async function fetchEntries() {
    try {
      setLoading(true);
      const res = await fetch('/api/candidate/career-history', { credentials: 'include' });
      if (!res.ok) throw new Error('Kunne ikke hente karriereprofil');
      const data = await res.json();
      setEntries(data.items || []);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  function handleSearch(q: string) {
    setSearchQuery(q);
    setSelectedOccupation(null);
    if (searchTimeout.current) window.clearTimeout(searchTimeout.current);
    if (q.length < 2) {
      setSearchResults([]);
      return;
    }
    searchTimeout.current = window.setTimeout(async () => {
      setSearching(true);
      try {
        const res = await fetch(
          `/api/candidate/career-history/esco/search?q=${encodeURIComponent(q)}&limit=15`,
          { credentials: 'include' }
        );
        if (res.ok) {
          const data = await res.json();
          setSearchResults(data.items || []);
        }
      } catch {} finally {
        setSearching(false);
      }
    }, 300);
  }

  function selectOccupation(occ: OccupationSearchResult) {
    setSelectedOccupation(occ);
    setSearchQuery(occ.title);
    setSearchResults([]);
  }

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!selectedOccupation) {
      setError('Velg en yrkesrolle fra søket');
      return;
    }
    if (!fromMonth || !fromYear) {
      setError('Velg fra-dato (måned og år)');
      return;
    }
    const fromDate = `${fromYear}-${fromMonth.padStart(2, '0')}-01`;
    let toDate: string | null = null;
    if (!stillCurrentlyEmployed) {
      if (!toMonth || !toYear) {
        setError('Velg til-dato eller marker som "pågående"');
        return;
      }
      toDate = `${toYear}-${toMonth.padStart(2, '0')}-01`;
      if (toDate < fromDate) {
        setError('Til-dato må være etter fra-dato');
        return;
      }
    }
    const pct = parseInt(percentage, 10);
    if (!Number.isInteger(pct) || pct < 1 || pct > 200) {
      setError('Stillingsprosent må være mellom 1 og 200');
      return;
    }

    setSaving(true);
    try {
      const res = await fetch('/api/candidate/career-history', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          esco_occupation_uri: selectedOccupation.uri,
          occupation_title: selectedOccupation.title,
          from_month: fromDate,
          to_month: toDate,
          percentage: pct,
          employer_name: employerName.trim() || null
        })
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Kunne ikke lagre');
        return;
      }
      // Reset form
      setShowAddForm(false);
      setSearchQuery('');
      setSelectedOccupation(null);
      setFromMonth(''); setFromYear('');
      setToMonth(''); setToYear('');
      setStillCurrentlyEmployed(false);
      setPercentage('100');
      setEmployerName('');
      await fetchEntries();
    } catch (e: any) {
      setError(e.message || 'Nettverksfeil');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Vil du fjerne denne oppføringen fra karriereprofilen din?')) return;
    try {
      const res = await fetch(`/api/candidate/career-history/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (!res.ok) {
        const data = await res.json();
        setError(data.error || 'Kunne ikke slette');
        return;
      }
      await fetchEntries();
    } catch (e: any) {
      setError(e.message || 'Nettverksfeil');
    }
  }

  const overlappingIds = detectOverlaps(entries);
  const hasOpenPosition = entries.some(e => e.to_month === null);
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 60 }, (_, i) => currentYear - i);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-kf-gray-200 p-6 mb-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold text-kf-gray-900 flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-kf-blue-600" />
            Min karriereprofil
          </h2>
          <p className="text-sm text-kf-gray-600 mt-1">
            Yrkesroller du har hatt eller har nå. Vi bruker disse til å foreslå relevante kurs i Læringssti.
          </p>
        </div>
        {!showAddForm && (
          <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
            <KarrierepassetImportWorkExperience onImported={fetchEntries} />
            <button
              onClick={() => setShowAddForm(true)}
              className="px-4 py-2 bg-kf-blue-600 text-white rounded-lg hover:bg-kf-blue-700 transition flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Legg til
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Add form */}
      {showAddForm && (
        <form onSubmit={handleAdd} className="border border-kf-blue-200 rounded-lg p-4 bg-kf-blue-50 mb-4 space-y-3">
          {/* Søk ESCO */}
          <div className="relative">
            <label className="block text-sm font-medium text-kf-gray-700 mb-1">Yrkesrolle</label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-kf-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder="Søk i ESCO yrkesregister (f.eks. sykepleier, regnskapsfører)..."
                className="w-full pl-10 pr-4 py-2 border border-kf-gray-300 rounded-lg focus:ring-2 focus:ring-kf-blue-500 focus:border-transparent"
                required
              />
              {searching && (
                <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-kf-gray-400 animate-spin" />
              )}
            </div>
            {selectedOccupation && (
              <div className="mt-2 px-3 py-2 bg-green-50 border border-green-200 rounded text-sm text-green-800 flex items-center gap-2">
                <Check className="w-4 h-4" />
                Valgt: <strong>{selectedOccupation.title}</strong>
                {selectedOccupation.essential_skill_count > 0 && (
                  <span className="text-xs text-green-600">
                    ({selectedOccupation.essential_skill_count} ferdigheter knyttet til yrket)
                  </span>
                )}
              </div>
            )}
            {searchResults.length > 0 && !selectedOccupation && (
              <ul className="absolute z-10 mt-1 w-full bg-white border border-kf-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {searchResults.map((r) => (
                  <li
                    key={r.uri}
                    onClick={() => selectOccupation(r)}
                    className="px-3 py-2 hover:bg-kf-blue-50 cursor-pointer border-b border-kf-gray-100 last:border-0"
                  >
                    <div className="font-medium text-sm">{r.title}</div>
                    {r.title_en && r.title_en !== r.title_no && (
                      <div className="text-xs text-kf-gray-500">{r.title_en}</div>
                    )}
                    {r.essential_skill_count > 0 && (
                      <div className="text-xs text-kf-blue-600 mt-1">
                        {r.essential_skill_count} ferdigheter registrert
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Arbeidsgiver */}
          <div>
            <label className="block text-sm font-medium text-kf-gray-700 mb-1">Arbeidsgiver (valgfritt)</label>
            <input
              type="text"
              value={employerName}
              onChange={(e) => setEmployerName(e.target.value)}
              placeholder="F.eks. Oslo kommune, Equinor..."
              className="w-full px-3 py-2 border border-kf-gray-300 rounded-lg"
            />
          </div>

          {/* Fra-dato */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-kf-gray-700 mb-1">Fra måned</label>
              <select
                value={fromMonth}
                onChange={(e) => setFromMonth(e.target.value)}
                className="w-full px-3 py-2 border border-kf-gray-300 rounded-lg"
                required
              >
                <option value="">Velg...</option>
                {MONTHS_NB.map((m, i) => (
                  <option key={i} value={String(i + 1)}>{m}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-kf-gray-700 mb-1">Fra år</label>
              <select
                value={fromYear}
                onChange={(e) => setFromYear(e.target.value)}
                className="w-full px-3 py-2 border border-kf-gray-300 rounded-lg"
                required
              >
                <option value="">Velg...</option>
                {yearOptions.map(y => <option key={y} value={String(y)}>{y}</option>)}
              </select>
            </div>
          </div>

          {/* Pågående checkbox */}
          <label className="flex items-center gap-2 text-sm text-kf-gray-700">
            <input
              type="checkbox"
              checked={stillCurrentlyEmployed}
              onChange={(e) => setStillCurrentlyEmployed(e.target.checked)}
              className="rounded"
            />
            Jeg har denne stillingen nå
          </label>

          {/* Til-dato (kun hvis ikke pågående) */}
          {!stillCurrentlyEmployed && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-kf-gray-700 mb-1">Til måned</label>
                <select
                  value={toMonth}
                  onChange={(e) => setToMonth(e.target.value)}
                  className="w-full px-3 py-2 border border-kf-gray-300 rounded-lg"
                >
                  <option value="">Velg...</option>
                  {MONTHS_NB.map((m, i) => (
                    <option key={i} value={String(i + 1)}>{m}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-kf-gray-700 mb-1">Til år</label>
                <select
                  value={toYear}
                  onChange={(e) => setToYear(e.target.value)}
                  className="w-full px-3 py-2 border border-kf-gray-300 rounded-lg"
                >
                  <option value="">Velg...</option>
                  {yearOptions.map(y => <option key={y} value={String(y)}>{y}</option>)}
                </select>
              </div>
            </div>
          )}

          {/* Stillingsprosent */}
          <div>
            <label className="block text-sm font-medium text-kf-gray-700 mb-1">
              Stillingsprosent <span className="text-xs text-kf-gray-500">(1-200%, kan være over 100% ved flere stillinger)</span>
            </label>
            <input
              type="number"
              min={1}
              max={200}
              value={percentage}
              onChange={(e) => setPercentage(e.target.value)}
              className="w-32 px-3 py-2 border border-kf-gray-300 rounded-lg"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => { setShowAddForm(false); setError(null); }}
              className="px-4 py-2 text-kf-gray-700 hover:bg-kf-gray-100 rounded-lg"
            >
              Avbryt
            </button>
            <button
              type="submit"
              disabled={saving || !selectedOccupation}
              className="px-4 py-2 bg-kf-blue-600 text-white rounded-lg hover:bg-kf-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              Lagre
            </button>
          </div>
        </form>
      )}

      {/* Liste */}
      {loading ? (
        <div className="text-center py-8 text-kf-gray-500">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
          Laster...
        </div>
      ) : entries.length === 0 ? (
        <div className="text-center py-8 text-kf-gray-500">
          <Briefcase className="w-12 h-12 mx-auto mb-2 text-kf-gray-300" />
          <p>Ingen yrkesroller registrert ennå.</p>
          <p className="text-sm mt-1">Legg til dine yrker for å få bedre kursforslag.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {!hasOpenPosition && entries.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-start gap-2 text-sm">
              <AlertCircle className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
              <p className="text-yellow-900">
                Ingen pågående stilling registrert. Vi vekter karriereveiledning og jobbsøkerkurs høyere i Læringssti.
              </p>
            </div>
          )}
          {entries.map((entry) => (
            <div
              key={entry.id}
              className={`border rounded-lg p-3 flex items-start justify-between ${
                overlappingIds.includes(entry.id) ? 'border-amber-300 bg-amber-50' : 'border-kf-gray-200 bg-white'
              }`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <strong className="text-kf-gray-900">{entry.occupation_title}</strong>
                  {entry.to_month === null && (
                    <span className="px-2 py-0.5 text-xs bg-green-100 text-green-800 rounded-full">Pågående</span>
                  )}
                  {entry.percentage !== 100 && (
                    <span className="px-2 py-0.5 text-xs bg-kf-gray-100 text-kf-gray-700 rounded-full">{entry.percentage}%</span>
                  )}
                </div>
                <div className="text-sm text-kf-gray-600 mt-1">
                  {fmtMonth(entry.from_month)} – {fmtMonth(entry.to_month)}
                </div>
                {entry.employer_name && (
                  <div className="text-sm text-kf-gray-500 flex items-center gap-1 mt-0.5">
                    <Building2 className="w-3 h-3" />
                    {entry.employer_name}
                  </div>
                )}
                {overlappingIds.includes(entry.id) && (
                  <div className="text-xs text-amber-700 mt-1 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    Overlapper med annen periode (helt OK ved deltidsstillinger eller dobbeltstillinger)
                  </div>
                )}
              </div>
              <button
                onClick={() => handleDelete(entry.id)}
                className="ml-2 p-2 text-kf-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                aria-label="Slett"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
